'use client';

import { useMemo } from 'react';

import classnames from 'classnames';

import { Card } from '../../card';
import { ValueChart } from '../../charts/value-chart/ValueChart';
import { AssessmentAttemptResult } from '../assessment-attempt-result/AssessmentAttemptResult';
import { AssessmentIndex } from '../multiple-choice-assessment/layout/assessment-index/AssessmentIndex';
import type { MultipleChoiceAssessmentAnswersProps } from './MultipleChoiceAssessmentAnswers.types';
import { ResultActions } from './layout/ResultActions';
import { SubjectMetrics } from './layout/subject-metrics/SubjectMetrics';

import styles from './MultipleChoiceAssessmentAnswers.module.scss';

function MultipleChoiceAssessmentAnswers({
  style,
  className,
  score,
  penalty,
  description,
  elapsedTime,
  answerRate,
  questions,
  scoreboard,
  official,
  onSelect,
  onRetry,
  testId = 'ui-multiple-choice-assessment-answers',
}: MultipleChoiceAssessmentAnswersProps) {
  const answered =
    answerRate.correct + answerRate.incorrect + answerRate.skipped;

  const correctPercentage = answered
    ? (answerRate.correct / answered) * 100
    : 0;

  const averageTimePerQuestion = questions.length
    ? elapsedTime / questions.length
    : 0;

  // Left unsorted: SubjectMetrics orders the rows itself, and by whichever column the
  // reader picks.
  const subjectMetrics = useMemo(() => {
    const grouped = new Map<string, { total: number; correct: number }>();

    for (const q of questions) {
      if (!q.subject) continue;
      const entry = grouped.get(q.subject) ?? { total: 0, correct: 0 };
      entry.total += 1;
      if (q.selectedOptionIndex === q.correctOptionIndex) entry.correct += 1;
      grouped.set(q.subject, entry);
    }

    return Array.from(grouped, ([subject, { total, correct }]) => ({
      subject,
      total,
      correct,
    }));
  }, [questions]);

  return (
    <div
      style={style}
      className={classnames(styles['main'], className)}
      data-testid={testId}
    >
      <AssessmentAttemptResult
        score={score}
        description={description}
        elapsedTime={elapsedTime}
        answerRate={answerRate}
      >
        <ResultActions
          answerRate={answerRate}
          scoreboard={scoreboard}
          official={official}
          onRetry={onRetry}
        />
      </AssessmentAttemptResult>

      <div className={styles['metrics']}>
        <Card title="Aciertos">
          <ValueChart
            value={correctPercentage}
            suffix="%"
            format={{ maximumFractionDigits: 1 }}
          />
        </Card>
        <Card title="Penalización">
          <ValueChart
            value={penalty}
            suffix=" pts"
            format={{ maximumFractionDigits: 2 }}
          />
        </Card>
        <Card title="Tiempo por pregunta">
          <ValueChart
            value={averageTimePerQuestion}
            suffix=" s"
            format={{ maximumFractionDigits: 2 }}
          />
        </Card>
      </div>

      {subjectMetrics.length > 0 && (
        <SubjectMetrics subjects={subjectMetrics} />
      )}
      <Card title="Preguntas">
        <AssessmentIndex
          className={styles['index']}
          questions={questions}
          fluid={true}
          onSelect={onSelect}
        />
      </Card>
    </div>
  );
}

export { MultipleChoiceAssessmentAnswers };
